import React, { useState } from "react";
import {
  Container,
  Modal,
  ModalBody,
  Button,
  Input,
  Label,
  Alert,
} from "reactstrap";
import { useSelector } from "react-redux";
import withRouter from "../../../components/Common/withRouter";
import { CardGridCustom } from "../../../components/Common/CardGridCustom";
import BizinBackend from "../../../helpers/bizin_helper";
import useGetCsrfToken from "../../../Hooks/useGetCsrfToken";

const REJECTION_REASONS = [
  "FRETE DIVERGENTE",
  "IPI DIVERGENTE",
  "MATERIAL/ITEM INCORRETO",
  "PREÇO DIVERGENTE",
  "DECLÍNIO DO PEDIDO",
  "CONDIÇÃO DE PAGAMENTO DIVERGENTE",
  "DADOS DO FORNECEDOR INCORRETOS",
  "PEDIDO MÍNIMO NÃO ATINGIDO",
  "PRAZO DE ENTREGA DIVERGENTE",
  "ENDEREÇO DE ENTREGA INCOMPLETO NA OC",
  "ITEM DO PEDIDO FORA DO MÚLTIPLO",
];

const DF_MO0006 = () => {
  const csrfToken = useGetCsrfToken();

  const queryParams = new URLSearchParams(window.location.search);
  const fileIdRaw = queryParams.get("ID_Arquivo");
  const ID_Arquivo = fileIdRaw ? parseInt(fileIdRaw, 10) : null;

  const { accessTokenMsman, userProfile } = useSelector((state) => ({
    accessTokenMsman: state.login.user?.access_token,
    userProfile: state.login.user,
  }));

  const ID_Usuario =
    userProfile?.resultset_2?.[0]?.ID_Usuario ??
    userProfile?.ID_Usuario ??
    null;

  const ID_Ultima_UE = 252;

  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [isRejectionModalOpen, setIsRejectionModalOpen] = useState(false);
  const [isAlertModalOpen, setIsAlertModalOpen] = useState(false);

  const [alertMessage, setAlertMessage] = useState("");
  const [isAcceptLoading, setIsAcceptLoading] = useState(false);
  const [isRejectionLoading, setIsRejectionLoading] = useState(false);

  const [selectedRejectionReason, setSelectedRejectionReason] = useState(
    REJECTION_REASONS[0],
  );
  const [rejectionJustification, setRejectionJustification] = useState("");

  const isInvalidFileId = !ID_Arquivo || Number.isNaN(ID_Arquivo);

  const toggleSuccessModal = () => {
    setIsSuccessModalOpen(!isSuccessModalOpen);
  };

  const toggleRejectionModal = () => {
    setIsRejectionModalOpen(!isRejectionModalOpen);
  };

  const toggleAlertModal = () => {
    setIsAlertModalOpen(!isAlertModalOpen);
  };

  const openAlertModal = (message) => {
    setAlertMessage(message);
    setIsAlertModalOpen(true);
  };

  const validatePrerequisites = () => {
    if (isInvalidFileId) {
      throw new Error("Valores inseridos não são válidos.");
    }

    if (!ID_Usuario) {
      throw new Error("Erro na montagem de requisição para envio.");
    }

    if (!csrfToken || !accessTokenMsman) {
      throw new Error("Erro na montagem de requisição para envio.");
    }
  };

  const validateProcedureResponse = (response, procedureName) => {
    if (response?.status && response.status !== 200) {
      throw new Error(
        response?.responseBackend ||
          response?.statusText ||
          `Erro ao executar a procedure ${procedureName}.`,
      );
    }

    return response;
  };

  const executeManualAcceptanceProcedure = () =>
    BizinBackend.executeProcedure(
      {
        1: ID_Arquivo,
        2: ID_Usuario,
      },
      "SRM_Insere_Validacao_Log_Arquivos_Aceite_Manual",
      csrfToken,
      accessTokenMsman,
    ).then((response) =>
      validateProcedureResponse(
        response,
        "SRM_Insere_Validacao_Log_Arquivos_Aceite_Manual",
      ),
    );

  const executeManualRejectionProcedure = () =>
    BizinBackend.executeProcedure(
      {
        1: ID_Arquivo,
        2: ID_Usuario,
        3: selectedRejectionReason,
        4: rejectionJustification,
      },
      "SRM_Insere_Validacao_Log_Arquivos_Recusa_Manual",
      csrfToken,
      accessTokenMsman,
    ).then((response) =>
      validateProcedureResponse(
        response,
        "SRM_Insere_Validacao_Log_Arquivos_Recusa_Manual",
      ),
    );

  const executeReceiptConfirmationPreparationProcedure = () =>
    BizinBackend.executeProcedure(
      {
        1: ID_Arquivo,
      },
      "UEX_PEDIDO_MRV_PREPARA_CONFIRMACAO_RECEBIMENTO_v2",
      csrfToken,
      accessTokenMsman,
    ).then((response) =>
      validateProcedureResponse(
        response,
        "UEX_PEDIDO_MRV_PREPARA_CONFIRMACAO_RECEBIMENTO_v2",
      ),
    );

  const executeManualReprocessingProcedure = () =>
    BizinBackend.executeProcedure(
      {
        1: ID_Arquivo,
        2: ID_Usuario,
        3: ID_Ultima_UE,
      },
      "EDI_Kernel_Set_Reprocessamento_Manual",
      csrfToken,
      accessTokenMsman,
    ).then((response) =>
      validateProcedureResponse(
        response,
        "EDI_Kernel_Set_Reprocessamento_Manual",
      ),
    );

  const executeAmancoControlProcedure = (action) => {
    const currentDate = new Date();

    const processStartDate =
      String(currentDate.getDate()).padStart(2, "0") +
      "/" +
      String(currentDate.getMonth() + 1).padStart(2, "0") +
      "/" +
      currentDate.getFullYear() +
      " " +
      String(currentDate.getHours()).padStart(2, "0") +
      ":" +
      String(currentDate.getMinutes()).padStart(2, "0") +
      ":" +
      String(currentDate.getSeconds()).padStart(2, "0");

    return BizinBackend.executeProcedure(
      {
        1: ID_Arquivo,
        2: 0,
        3: processStartDate,
        4: ID_Usuario,
        5: action,
      },
      "EDI_User_Exits_Controle_Add_Amanco",
      csrfToken,
      accessTokenMsman,
    ).then((response) =>
      validateProcedureResponse(response, "EDI_User_Exits_Controle_Add_Amanco"),
    );
  };

  const executeAcceptanceRejectionFlagProcedure = () =>
    BizinBackend.executeProcedure(
      {
        1: ID_Arquivo,
        2: "P",
      },
      "EDI_Arquivos_Grava_Flag_AceiteRecusa",
      csrfToken,
      accessTokenMsman,
    ).then((response) =>
      validateProcedureResponse(
        response,
        "EDI_Arquivos_Grava_Flag_AceiteRecusa",
      ),
    );

  const executeAcceptanceFlow = () =>
    Promise.all([
      executeManualAcceptanceProcedure(),
      executeReceiptConfirmationPreparationProcedure(),
      executeManualReprocessingProcedure(),
      executeAmancoControlProcedure("aceite"),
      executeAcceptanceRejectionFlagProcedure(),
    ]);

  const executeRejectionFlow = () =>
    Promise.all([
      executeManualRejectionProcedure(),
      executeReceiptConfirmationPreparationProcedure(),
      executeManualReprocessingProcedure(),
      executeAmancoControlProcedure("recusa"),
      executeAcceptanceRejectionFlagProcedure(),
    ]);

  const handleAccept = async () => {
    try {
      validatePrerequisites();
      setIsAcceptLoading(true);
      await executeAcceptanceFlow();
      setIsSuccessModalOpen(true);
    } catch (error) {
      openAlertModal(error?.message || "Erro no envio do aceite.");
    } finally {
      setIsAcceptLoading(false);
    }
  };

  const handleRejection = () => {
    setIsRejectionModalOpen(true);
  };

  const handleConfirmRejection = async () => {
    try {
      validatePrerequisites();

      if (!selectedRejectionReason) {
        openAlertModal("Selecione o motivo da recusa.");
        return;
      }

      if (!rejectionJustification.trim()) {
        openAlertModal("Informe a justificativa da recusa.");
        return;
      }

      setIsRejectionLoading(true);
      await executeRejectionFlow();
      setIsRejectionModalOpen(false);
      setIsSuccessModalOpen(true);
    } catch (error) {
      openAlertModal(error?.message || "Erro no envio da recusa.");
    } finally {
      setIsRejectionLoading(false);
    }
  };

  if (isInvalidFileId) {
    return (
      <div className="page-content">
        <Container
          fluid
          className="dataTable-custom-primary d-flex flex-column justify-content-center vh-100"
        >
          <div className="row justify-content-center">
            <div className="col-md-8 col-lg-6">
              <Alert color="danger" className="text-center mb-0">
                Não é possível acessar essa transação sem parâmetros.
              </Alert>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container
        fluid
        className="dataTable-custom-primary d-flex flex-column justify-content-center vh-100"
      >
        <div className="text-center mt-4 mb-4">
          <div className="mb-3">
            <i className="far fa-file-lines fa-3x text-primary"></i>
          </div>

          <h5 className="m-0 font-size-20 font-w-600 text-primary-dark">
            Parâmetros de Aceite ou Recusa manual
          </h5>

          <p className="text-muted mt-2 mb-0">
            O parâmetro selecionado nesta tela será utilizado para determinar o
            aceite ou a recusa manual.
          </p>
        </div>

        <div className="row justify-content-center">
          <div className="col-lg-8 col-xl-7">
            <CardGridCustom>
              <div className="text-center p-3">
                <h5 className="m-0 font-size-20 font-w-600 text-primary-dark">
                  Selecione a operação que será executada no sistema destino
                </h5>

                <div className="d-flex justify-content-center flex-wrap gap-4 mt-4">
                  <button
                    type="button"
                    className="button-filter-action font-w-600 bg-primary-dark border-primary-dark text-white px-4 btn-hover-primary btn btn-secondary"
                    onClick={handleAccept}
                    disabled={isAcceptLoading || isRejectionLoading}
                  >
                    <i className="far fa-check-circle me-2"></i>
                    {isAcceptLoading ? "Processando..." : "Aceite do Pedido"}
                  </button>

                  <button
                    type="button"
                    className="button-filter-action font-w-600 bg-primary-dark border-primary-dark text-white px-4 btn-hover-primary btn btn-secondary"
                    onClick={handleRejection}
                    disabled={isAcceptLoading || isRejectionLoading}
                  >
                    <i className="far fa-times-circle me-2"></i>
                    Recusa do Pedido
                  </button>
                </div>
              </div>
            </CardGridCustom>
          </div>
        </div>

        <Modal
          isOpen={isRejectionModalOpen}
          toggle={toggleRejectionModal}
          centered
          size="lg"
        >
          <ModalBody className="px-4 py-4">
            <div className="text-center mb-4">
              <div className="mb-3">
                <i className="far fa-file-lines fa-3x text-primary"></i>
              </div>

              <h4 className="m-0 font-size-20 font-w-600 text-primary-dark">
                Parâmetros de Aceite ou Recusa Manual
              </h4>

              <p className="text-muted mt-2 mb-0">
                O parâmetro selecionado nesta tela será utilizado para
                determinar o aceite ou a recusa manual.
              </p>
            </div>

            <div className="col-12 col-md-10 col-lg-8 mx-auto">
              <div className="mb-4">
                <Label className="font-w-600 text-primary-dark mb-2">
                  Selecione a operação que será executada no sistema destino
                </Label>

                <Input
                  type="select"
                  value={selectedRejectionReason}
                  onChange={(e) => setSelectedRejectionReason(e.target.value)}
                >
                  {REJECTION_REASONS.map((reason) => (
                    <option key={reason} value={reason}>
                      {reason}
                    </option>
                  ))}
                </Input>
              </div>

              <div className="mb-4">
                <Label className="font-w-600 text-primary-dark mb-2">
                  Justificativa da Recusa
                </Label>

                <Input
                  type="textarea"
                  rows="6"
                  value={rejectionJustification}
                  onChange={(e) => setRejectionJustification(e.target.value)}
                />
              </div>

              <div className="d-flex justify-content-start">
                <Button
                  type="button"
                  className="button-filter-action font-w-600 bg-primary-dark border-primary-dark text-white px-4 btn-hover-primary btn btn-secondary"
                  onClick={handleConfirmRejection}
                  disabled={isRejectionLoading}
                >
                  {isRejectionLoading ? "Processando..." : "Confirmar Recusa"}
                </Button>
              </div>
            </div>
          </ModalBody>
        </Modal>

        <Modal isOpen={isSuccessModalOpen} toggle={toggleSuccessModal} centered>
          <ModalBody className="text-center px-4 py-5">
            <div className="mb-4">
              <i className="far fa-check-circle text-success fa-5x"></i>
            </div>

            <h2 className="mb-3 font-w-600 text-dark">Sucesso!</h2>

            <p className="text-muted mb-4">
              O Pedido foi enviado para processamento, verifique-o novamente
              mais tarde
            </p>

            <Button
              type="button"
              className="button-filter-action font-w-600 bg-primary-dark border-primary-dark text-white px-4 btn-hover-primary btn btn-secondary"
              onClick={toggleSuccessModal}
            >
              OK
            </Button>
          </ModalBody>
        </Modal>

        <Modal isOpen={isAlertModalOpen} toggle={toggleAlertModal} centered>
          <ModalBody className="text-center px-4 py-5">
            <div className="mb-4">
              <i className="far fa-exclamation-circle text-warning fa-5x"></i>
            </div>

            <h2 className="mb-3 font-w-600 text-dark">Alerta!</h2>

            <p className="text-muted mb-4">{alertMessage}</p>

            <Button
              type="button"
              className="button-filter-action font-w-600 text-white px-4 btn btn-secondary"
              onClick={toggleAlertModal}
            >
              OK
            </Button>
          </ModalBody>
        </Modal>
      </Container>
    </div>
  );
};

export default withRouter(DF_MO0006);
